import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, FileText, User, Briefcase, GraduationCap, Award, BookOpen, Code, ChevronDown, ChevronUp, Save, Plus, LayoutGrid, Eye, Trash2, Undo2, Redo2, X, Download, GripVertical, RefreshCw, Palette, CheckCircle2 } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { preprocessResumeForReview } from '@soVendor/services/langchainService';
import { ResumeData } from '../shared/types';
import { parseResumeSections, arrangeResumeSections, bucketResumeSections, moveContactFromSummaryToPersonalInfo, type ResumeSection } from '../utils/parseResumeSections';
import { normalizeResumeText, preprocessSectionContent } from '@soVendor/utils/pdfTextSanitizer';
import { SectionContentWithFormatting } from '../utils/sectionContentRenderer';
import { STEP_WRAPPER_CLASS, STEP_MOTION_CLASS, STEP_PRIMARY_CTA_CLASS } from '../shared/stepLayout';
import { getTemplateSkinClass, TemplateThumbnail, TEMPLATE_LIBRARY } from '../ResultsStep';
import { getEducationDisplayContent } from '../utils/educationFields';
import { getWorkExperienceDisplayContent } from '../utils/workExperienceFields';
import { getAwardsDisplayContent } from '../utils/awardsFields';
import PersonalInfoForm from './PersonalInfoForm';
import EducationCard from './EducationCard';
import WorkExperienceCard from './WorkExperienceCard';
import AwardsCard from './AwardsCard';
import RichTextToolbar from './RichTextToolbar';
import MarkdownSectionEditor from './MarkdownSectionEditor';

/** Accent color palette for the Design panel — light, neutral tones that blend with any template */
const DESIGN_COLORS: Array<{
  label: string;
  swatch: string;
  accent: string;
  accentBg: string;
  accentText: string;
  accentBorder: string;
}> = [
  { label: 'Slate',    swatch: '#94a3b8', accent: '#94a3b8', accentBg: '#f1f5f9', accentText: '#334155', accentBorder: '#cbd5e1' },
  { label: 'Gray',     swatch: '#9ca3af', accent: '#9ca3af', accentBg: '#f3f4f6', accentText: '#374151', accentBorder: '#d1d5db' },
  { label: 'Stone',    swatch: '#a8a29e', accent: '#a8a29e', accentBg: '#f5f5f4', accentText: '#44403c', accentBorder: '#d6d3d1' },
  { label: 'Sage',     swatch: '#86b886', accent: '#6aaa6a', accentBg: '#f0f7f0', accentText: '#2d5a2d', accentBorder: '#b8d8b8' },
  { label: 'Mist',     swatch: '#93c5fd', accent: '#60a5fa', accentBg: '#eff6ff', accentText: '#1e40af', accentBorder: '#bfdbfe' },
  { label: 'Blush',    swatch: '#f9a8d4', accent: '#f472b6', accentBg: '#fdf2f8', accentText: '#9d174d', accentBorder: '#fbcfe8' },
];

/**
 * Templates shown in the native Design panel carousel — all templates,
 * light/soft ones first so users see the best-looking ones immediately.
 */
const PICKER_TEMPLATE_IDS = [
  // ── light / soft first ──────────────────────────────
  'soft-white', 'warm-cream', 'sage-light', 'pearl-rose',
  // ── minimal / white bg ──────────────────────────────
  'developer', 'clean-minimal', 'classic-serif', 'fresh-grad',
  // ── light blue ──────────────────────────────────────
  'ats-optimized', 'modern-blue', 'navy-tech', 'business-clean',
  'junior-tech', 'entry-business',
  // ── slate / gray ────────────────────────────────────
  'slate-pro', 'bold-dark', 'executive', 'faculty',
  // ── colored accent ──────────────────────────────────
  'marketing-pro', 'creative-bold', 'accounting', 'healthcare-pro',
  'nursing', 'health-admin', 'finance-pro',
  // ── serif / legal / academic ────────────────────────
  'legal-pro', 'research-scholar', 'academic-clean',
];

/** Section types user can add. Hover = preview in right pane; click row = add (no + on right by default). */
const ADDABLE_SECTION_TYPES: { title: string; description: string; sampleText: string; howToLabel?: string }[] = [
  { title: 'Education', description: 'Provide your educational background and courses.', sampleText: '09/2015 – 05/2019 · University of St Andrews\nMA English · Completed rigorous coursework.', howToLabel: 'How to write an education summary?' },
  { title: 'Work Experience', description: 'List roles, companies, dates, and key achievements.', sampleText: 'Sr. Technical Manager · NETCRACKER · 04/2019 – 10/2023\n• Total control of Product Platform', howToLabel: 'How to list work experience?' },
  { title: 'Awards', description: 'Highlight recognitions and awards.', sampleText: 'Stellar Success Award · Top performance\nAwarded for exceptional contribution.', howToLabel: 'How to list awards on a resume?' },
  { title: 'Certifications', description: 'Add professional certifications.', sampleText: 'Sun Certified Java Programmer (SCJP 5.0)\nDigital Marketing NanoDegree (DMND - 2019)' },
  { title: 'Skills', description: 'Showcase technical and soft skills.', sampleText: 'Languages: Java, Python, Spring\nCloud & DevOps: Kubernetes, Docker' },
  { title: 'Goal', description: 'State your career objective. Keep it concise.', sampleText: 'Seeking to launch my career as a junior writer at an established publishing house.' },
  { title: 'Projects', description: 'Describe key projects and outcomes.', sampleText: 'Rebranding & Content Overhaul · 05/2021 – 11/2021\nSkills: SEO Copywriting, Team Management' },
  { title: 'Publications', description: 'List articles, papers, or books.', sampleText: 'The Future of Customer Service in AI Era · Customer Support Hub · 08/2023' },
  { title: 'References', description: "Include contact information when you add references.", sampleText: '(123) 456 7890, JOHNDOE@EXAMPLE.COM\nJohn Doe · Company · Head of Customer Support.', howToLabel: 'How to put references on a resume?' },
  { title: 'Hobbies', description: 'Optional section for interests.', sampleText: 'Adventure · Reading' },
  { title: 'Achievements', description: 'Key accomplishments with impact.', sampleText: '• Resolved 98% of customer inquiries.\n• 95%+ customer satisfaction rate.' },
  { title: 'Languages', description: 'Languages you speak, with level if relevant.', sampleText: 'English, Portuguese (Basic), Spanish (Basic)' },
  { title: 'Text', description: 'Add custom content or notes.', sampleText: 'Add custom content or notes.' },
];


/** Only these sections get the structured form (name, phone, email, address, etc.). Professional Summary / Profile / Summary get plain title + rich text. */
const SECTIONS_USE_PERSONAL_INFO_FORM = new Set([
  'contact', 'contact information', 'personal information'
]);

function displaySectionTitle(section: ResumeSection, index: number, t: (key: string, fallback?: string) => string): string {
  const lower = section.title.toLowerCase().trim();
  if (section.title === 'Full Resume') return t('optimizer.resultsStep.fullResume', 'Full resume');
  if (index === 0 && (SECTIONS_USE_PERSONAL_INFO_FORM.has(lower) || lower.includes('contact') || lower.includes('personal information') || lower === 'general')) {
    return t('optimizer.resultsStep.personalInformation', 'Personal Information');
  }
  return section.title;
}

const SECTION_ICON_CLASS = 'w-5 h-5 shrink-0';

function sectionIcon(title: string, iconClass: string = 'w-4 h-4 shrink-0'): React.ReactNode {
  const lower = title.toLowerCase();
  const c = iconClass;
  if (lower.includes('personal') || lower.includes('contact')) return <User className={c} />;
  if (lower.includes('experience') || lower.includes('work')) return <Briefcase className={c} />;
  if (lower.includes('education')) return <GraduationCap className={c} />;
  if (lower.includes('award')) return <Award className={c} />;
  if (lower.includes('certif')) return <BookOpen className={c} />;
  if (lower.includes('skill') || lower.includes('language') || lower.includes('framework')) return <Code className={c} />;
  return <FileText className={c} />;
}

function isPersonalInfoSection(section: ResumeSection, index: number): boolean {
  const lower = section.title.toLowerCase().trim();
  return (
    SECTIONS_USE_PERSONAL_INFO_FORM.has(lower) ||
    (index === 0 && (lower.includes('contact') || lower.includes('personal information') || lower === 'general'))
  );
}

function isEducationSection(section: ResumeSection): boolean {
  return section.title.toLowerCase().trim().includes('education');
}

function isWorkExperienceSection(section: ResumeSection): boolean {
  const lower = section.title.toLowerCase().trim();
  return lower.includes('work experience') || lower.includes('employment') || lower.includes('experience');
}

function isAwardsSection(section: ResumeSection): boolean {
  return section.title.toLowerCase().trim().includes('award');
}

function getStructuredSectionDisplayContent(section: ResumeSection): string | undefined {
  if (isEducationSection(section)) return getEducationDisplayContent(section.content);
  if (isWorkExperienceSection(section)) return getWorkExperienceDisplayContent(section.content);
  if (isAwardsSection(section)) return getAwardsDisplayContent(section.content);
  return undefined;
}

interface ParsedResumeStepProps {
  resumeData: ResumeData;
  selectedTemplateId?: string;
  onNext: (updatedResumeData?: ResumeData) => void;
  onBack: () => void;
  onSave?: (updatedResumeData: ResumeData) => void;
  isGuest?: boolean;
  /** When true, hide top bar (Undo/Redo, Back, Continue) for use inside a 3-column layout */
  embedded?: boolean;
  /** When embedded, call with latest resume data when sections change (so parent can pass it on Continue) */
  onResumeDataChange?: (data: ResumeData) => void;
  /** Optional: called when user wants to discard the current session and start fresh */
  onStartNew?: () => void;
}

/**
 * Review step: after Upload + Choose template. Runs LLM preprocess (clean/sanitize), then shows two-pane (sections left, resume right) using full page space.
 */
const LEFT_PANE_MIN_PCT = 36;
const LEFT_PANE_MAX_PCT = 65;
const LEFT_PANE_DEFAULT_PCT = 55;

const ParsedResumeStep: React.FC<ParsedResumeStepProps> = ({
  resumeData,
  selectedTemplateId = 'ats-optimized',
  onNext,
  onBack,
  onSave,
  isGuest: _isGuest = false,
  embedded = false,
  onResumeDataChange,
  onStartNew,
}) => {
  const { t } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const previewRef = useRef<HTMLDivElement>(null);
  const paneContainerRef = useRef<HTMLDivElement>(null);
  const leftPaneScrollRef = useRef<HTMLDivElement>(null);
  const rightPaneScrollRef = useRef<HTMLDivElement>(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<number | null>(null);
  const [isPreprocessing, setIsPreprocessing] = useState<boolean>(true);
  const [preprocessError, setPreprocessError] = useState<string | null>(null);
  const [preprocessedSections, setPreprocessedSections] = useState<ResumeSection[]>([]);
  const [leftPaneWidthPct, setLeftPaneWidthPct] = useState<number>(LEFT_PANE_DEFAULT_PCT);
  const [isDraggingDivider, setIsDraggingDivider] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<'idle' | 'saved'>('idle');
  const [savedSectionIndex, setSavedSectionIndex] = useState<number | null>(null);
  const [sectionVisible, setSectionVisible] = useState<Record<number, boolean>>({});
  const [hoveredAddSection, setHoveredAddSection] = useState<string | null>(null);
  const [previewAllPopupOpen, setPreviewAllPopupOpen] = useState(false);
  const [undoState, setUndoState] = useState<{ past: ResumeSection[][]; future: ResumeSection[][] }>({ past: [], future: [] });
  const [isMd, setIsMd] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches);
  const [previewZoom, setPreviewZoom] = useState(0.98);
  // Mobile tab: show either section list OR preview pane (not both stacked)
  const [mobileTab, setMobileTab] = useState<'sections' | 'preview'>('sections');
  // Mobile add-section bottom sheet
  const [mobileAddSectionOpen, setMobileAddSectionOpen] = useState(false);
  // Templates persistent drawer + active template (local, overrides prop)
  const [templatesDrawerOpen, setTemplatesDrawerOpen] = useState(false);
  const [templatesDrawerExpanded, setTemplatesDrawerExpanded] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string>(selectedTemplateId);
  // Design bottom sheet (mobile)
  const [designSheetExpanded, setDesignSheetExpanded] = useState(false);
  // Active bottom panel on native: null | 'design' | 'addsection'
  const [activePanel, setActivePanel] = useState<null | 'design' | 'addsection'>(null);
  // Design customizations — applied live to the resume preview
  const [designColor, setDesignColor] = useState<string | null>(null);
  const [designFont, setDesignFont] = useState<'default' | 'serif' | 'compact'>('default');
  const [designCaps, setDesignCaps] = useState<'title' | 'upper'>('upper');
  // Drag gesture on design drawer handle
  const dragStartY = useRef<number | null>(null);
  const tWithFallback = (key: string, fallback?: string) => (fallback != null ? t(key, fallback) : t(key));

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsMd(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  // Use sections from UploadStep (which already ran LLM structuring) when available,
  // avoiding a duplicate LLM call that wastes tokens and hits rate limits.
  // Only fall back to preprocessResumeForReview when UploadStep didn't produce sections.
  useEffect(() => {
    let cancelled = false;
    const rawText = resumeData.parsedText || '';
    if (!rawText.trim() || rawText.trim().length < 100) {
      setPreprocessedSections([]);
      setIsPreprocessing(false);
      return;
    }

    const existing = resumeData.sections;
    const hasValidSections = Array.isArray(existing) && existing.length >= 3
      && existing.every((s: unknown) => s && typeof (s as any).title === 'string'
        && typeof (s as any).content === 'string' && ((s as any).content || '').trim().length > 0);

    if (hasValidSections) {
      const cleaned = (existing as ResumeSection[]).map((s) => ({
        ...s,
        content: preprocessSectionContent(s.content || ''),
        title: (s.title || '').trim(),
      }));
      setPreprocessedSections(moveContactFromSummaryToPersonalInfo(cleaned));
      setIsPreprocessing(false);
      return;
    }

    setPreprocessError(null);
    setPreprocessedSections([]);
    setIsPreprocessing(true);
    preprocessResumeForReview(rawText)
      .then((llmSections) => {
        if (cancelled) return;
        if (llmSections && llmSections.length > 0) {
          const cleaned = (llmSections as ResumeSection[]).map((s) => ({
            ...s,
            content: preprocessSectionContent(s.content || ''),
            title: (s.title || '').trim(),
          }));
          setPreprocessedSections(moveContactFromSummaryToPersonalInfo(cleaned));
        } else {
          const normalized = normalizeResumeText(rawText);
          const parsed = parseResumeSections(normalized);
          const arranged = arrangeResumeSections(parsed);
          const bucketed = bucketResumeSections(arranged);
          setPreprocessedSections(bucketed);
        }
        setPreprocessError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreprocessError(err?.message || t('optimizer.preprocessFailed', 'Preprocessing failed'));
        const normalized = normalizeResumeText(rawText);
        const parsed = parseResumeSections(normalized);
        const arranged = arrangeResumeSections(parsed);
        const bucketed = bucketResumeSections(arranged);
        const cleaned = bucketed.map((s) => ({ ...s, content: preprocessSectionContent(s.content || '') }));
        setPreprocessedSections(moveContactFromSummaryToPersonalInfo(cleaned));
      })
      .finally(() => {
        if (!cancelled) setIsPreprocessing(false);
      });
    return () => { cancelled = true; };
  }, [resumeData.parsedText, resumeData.sections, t]);

  // After selecting template, keep all sections closed
  useEffect(() => {
    setExpandedSectionIndex(null);
  }, [selectedTemplateId]);

  const sections = preprocessedSections;
  const hasSections = sections.length > 1 || (sections.length === 1 && sections[0].title !== 'Full Resume');

  /* Prezi-like zoom: scale preview document based on scroll (closer in center, further at edges) */
  useEffect(() => {
    const el = rightPaneScrollRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const maxScroll = Math.max(0, scrollHeight - clientHeight);
        if (maxScroll <= 0) {
          setPreviewZoom(1);
          return;
        }
        const progress = scrollTop / maxScroll; // 0 at top, 1 at bottom
        const t = 2 * progress - 1; // -1 .. 1
        const scale = 0.96 + 0.08 * (1 - t * t); // peak 1.04 at center, 0.96 at edges
        setPreviewZoom(Math.min(1.04, Math.max(0.96, scale)));
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [hasSections, sections.length]);

  useEffect(() => {
    if (embedded && onResumeDataChange && sections.length > 0) {
      onResumeDataChange({ ...resumeData, sections: sections.length > 0 ? sections : resumeData.sections });
    }
  }, [embedded, onResumeDataChange, resumeData, sections]);

  const pushHistory = useCallback((current: ResumeSection[]) => {
    setUndoState((prev) => ({
      past: [...prev.past, current.map((s) => ({ ...s, content: s.content, title: s.title, preview: s.preview ?? '' }))].slice(-50),
      future: [],
    }));
  }, []);

  const handleUndo = useCallback(() => {
    setUndoState((prev) => {
      if (prev.past.length === 0) return prev;
      const restored = prev.past[prev.past.length - 1];
      setTimeout(() => setPreprocessedSections(restored.map((s) => ({ ...s }))), 0);
      return {
        past: prev.past.slice(0, -1),
        future: [sections, ...prev.future],
      };
    });
  }, [sections]);

  const handleRedo = useCallback(() => {
    setUndoState((prev) => {
      if (prev.future.length === 0) return prev;
      const restored = prev.future[0];
      setTimeout(() => setPreprocessedSections(restored.map((s) => ({ ...s }))), 0);
      return {
        past: [...prev.past, sections],
        future: prev.future.slice(1),
      };
    });
  }, [sections]);

  const updateSectionContent = useCallback((index: number, content: string) => {
    pushHistory(sections);
    setPreprocessedSections((prev) =>
      prev.map((s, i) =>
        i === index
          ? { ...s, content, preview: content.slice(0, 120).replace(/\n/g, ' ') + (content.length > 120 ? '…' : '') }
          : s
      )
    );
  }, [sections, pushHistory]);

  const updateSectionTitle = useCallback((index: number, title: string) => {
    pushHistory(sections);
    setPreprocessedSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, title: title.trim() || s.title } : s))
    );
  }, [sections, pushHistory]);

  const addSection = useCallback((sectionTitle: string) => {
    pushHistory(sections);
    const newSection: ResumeSection = {
      title: sectionTitle,
      content: '',
      preview: '',
    };
    const newIndex = sections.length;
    setPreprocessedSections((prev) => [...prev, newSection]);
    setExpandedSectionIndex(newIndex);
    setTimeout(() => {
      document.getElementById(`preview-section-${newIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [sections, pushHistory]);

  const scrollPreviewToTop = useCallback(() => {
    rightPaneScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /** Scrolls BOTH panes so the section is in view: left pane to accordion, right pane to preview block. */
  const scrollToSection = useCallback((index: number) => {
    const accordionEl = document.getElementById(`section-accordion-${index}`);
    const sectionEl = document.getElementById(`preview-section-${index}`);
    const leftScroll = leftPaneScrollRef.current;
    const rightScroll = rightPaneScrollRef.current;

    const run = () => {
      if (leftScroll && accordionEl) {
        const leftRect = leftScroll.getBoundingClientRect();
        const accordionRect = accordionEl.getBoundingClientRect();
        const accordionTopInLeft = accordionRect.top - leftRect.top + leftScroll.scrollTop;
        const targetLeft = Math.max(0, accordionTopInLeft - leftRect.height / 4);
        leftScroll.scrollTo({ top: targetLeft, behavior: 'smooth' });
      }
      if (rightScroll && sectionEl) {
        const rightRect = rightScroll.getBoundingClientRect();
        const sectionRect = sectionEl.getBoundingClientRect();
        const sectionTopInRight = sectionRect.top - rightRect.top + rightScroll.scrollTop;
        const targetRight = Math.max(0, sectionTopInRight - rightRect.height / 2 + sectionRect.height / 2);
        rightScroll.scrollTo({ top: Math.min(rightScroll.scrollHeight - rightRect.height, targetRight), behavior: 'smooth' });
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);

  const toggleSection = (index: number) => {
    setExpandedSectionIndex((prev) => {
      const isOpening = prev !== index;
      if (isOpening) setTimeout(() => scrollToSection(index), 120);
      return prev === index ? null : index;
    });
  };

  const focusSectionFromPreview = useCallback((index: number) => {
    setExpandedSectionIndex(index);
    // On mobile, switch to Fill In tab and close any open panel
    if (!isMd) {
      setMobileTab('sections');
      setActivePanel(null);
    }
    setTimeout(() => scrollToSection(index), 80);
  }, [scrollToSection, isMd]);

  const isSectionVisibleInPreview = (index: number) => sectionVisible[index] !== false;
  const toggleSectionVisibility = (index: number) => {
    setSectionVisible((prev) => ({ ...prev, [index]: !(prev[index] !== false) }));
  };
  const duplicateSection = (index: number) => {
    pushHistory(sections);
    const copy: ResumeSection = { ...sections[index], title: sections[index].title + ' (copy)', preview: (sections[index].content || '').slice(0, 120).replace(/\n/g, ' ') + '…' };
    setPreprocessedSections((prev) => [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)]);
    setExpandedSectionIndex(index + 1);
    setSectionVisible((prev) => ({ ...prev, [index + 1]: true }));
  };
  const removeSection = (index: number) => {
    pushHistory(sections);
    setPreprocessedSections((prev) => prev.filter((_, i) => i !== index));
    setExpandedSectionIndex((prev) => (prev == null || prev === index ? null : prev > index ? prev - 1 : prev));
    setSectionVisible((prev) => {
      const next = { ...prev };
      delete next[index];
      const shifted: Record<number, boolean> = {};
      Object.keys(next).forEach((k) => { const i = Number(k); if (i > index) shifted[i - 1] = next[i]; else shifted[i] = next[i]; });
      return shifted;
    });
  };

  const handleContinue = () => {
    const updated: ResumeData = {
      ...resumeData,
      sections: sections.length > 0 ? sections : resumeData.sections
    };
    onNext(updated);
  };

  const getUpdatedResumeData = useCallback((): ResumeData => ({
    ...resumeData,
    sections: sections.length > 0 ? sections : resumeData.sections
  }), [resumeData, sections]);

  const handleSave = useCallback(() => {
    if (!onSave) return;
    onSave(getUpdatedResumeData());
    setSaveFeedback('saved');
    setTimeout(() => setSaveFeedback('idle'), 2000);
  }, [onSave, getUpdatedResumeData]);

  const handleSaveFromSection = useCallback((sectionIndex: number) => {
    if (!onSave) return;
    onSave(getUpdatedResumeData());
    setSaveFeedback('saved');
    setSavedSectionIndex(sectionIndex);
    setTimeout(() => {
      setSaveFeedback('idle');
      setSavedSectionIndex(null);
    }, 2000);
  }, [onSave, getUpdatedResumeData]);

  // Resizable divider: pointer drag to adjust left pane width (middle = room for sections)
  useEffect(() => {
    if (!isDraggingDivider) return;
    const container = paneContainerRef.current;
    const onMove = (e: PointerEvent) => {
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const pct = Math.round((x / rect.width) * 100);
      setLeftPaneWidthPct(Math.min(LEFT_PANE_MAX_PCT, Math.max(LEFT_PANE_MIN_PCT, pct)));
    };
    const onUp = () => setIsDraggingDivider(false);
    document.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingDivider]);

  if (isPreprocessing) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={isNative ? '' : `${STEP_MOTION_CLASS} h-full min-h-0 flex flex-col`}
        style={isNative ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', alignItems: 'center', justifyContent: 'center', isolation: 'isolate' as const } : undefined}
      >
        <div className={isNative ? 'flex flex-col items-center justify-center py-12' : `${STEP_WRAPPER_CLASS} flex-1 min-h-0 flex flex-col items-center justify-center py-12`}>
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-200 dark:border-gray-600 border-t-[#6C47FF] dark:border-t-[#5B8DFF] mb-4" />
          <p className="text-gray-700 dark:text-gray-300 font-medium">
            {t('optimizer.cleaningResume', 'Cleaning and structuring your resume…')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('optimizer.cleaningResumeHint', 'Removing clutter and organizing sections')}
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className={isNative ? 'w-full max-w-full' : `${STEP_MOTION_CLASS} h-full min-h-0 flex flex-col w-full max-w-full`}
      style={isNative ? { position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', isolation: 'isolate' as const } : undefined}
    >
      <div className="w-full max-w-full flex-1 min-h-0 flex flex-col px-0">
        {/* ── Native mobile header — title + Eye only (Back/Next moved to bottom) ── */}
        {!embedded && !isMd && isNative && (
          <div
            className="flex-shrink-0 flex items-center justify-between px-3 pb-3 gap-2"
            style={{
              background: 'var(--ggj-header-bg)',
              paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
              borderRadius: '0 0 24px 24px',
              boxShadow: 'var(--brand-cta-shadow, 0 4px 20px rgba(108,71,255,0.25))',
            }}
          >
            <div className="w-9 h-9 flex-shrink-0" aria-hidden />
            <div className="flex flex-col items-center flex-1 min-w-0">
              <span className="text-[10px] font-semibold uppercase tracking-widest leading-none" style={{ color: 'var(--ggj-header-sub)' }}>Resume Optimizer</span>
              <span className="text-sm font-bold leading-tight mt-0.5" style={{ color: 'var(--ggj-header-text)' }}>
                {activePanel === 'design' ? 'Live Preview' : mobileTab === 'sections' ? 'Edit Resume' : 'Preview'}
              </span>
              <span className="text-[10px] text-purple-200/70 mt-0.5">
                {sections.length > 0 ? `${sections.length} sections` : ''}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (activePanel === 'design') {
                  setActivePanel(null);
                  setMobileTab('preview');
                } else {
                  setMobileTab(prev => prev === 'preview' ? 'sections' : 'preview');
                  setActivePanel(null);
                }
              }}
              className={`w-9 h-9 flex items-center justify-center rounded-xl touch-manipulation transition-colors flex-shrink-0 ${
                mobileTab === 'preview' && activePanel === null
                  ? 'bg-white text-[#6C47FF]'
                  : 'bg-white/15 text-white'
              }`}
              aria-label={mobileTab === 'preview' ? 'Edit sections' : 'Preview resume'}
            >
              <Eye className="w-4.5 h-4.5" />
            </button>
          </div>
        )}

        {!embedded && (isMd || !isNative) && (
          /* Web / desktop top bar: neutral, theme-consistent */
          <div className="sticky top-0 z-10 flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            style={{ minHeight: 44, paddingTop: isNative ? 'var(--safe-area-top, 28px)' : undefined }}
          >
            {/* ── Left group: Back · divider · Undo/Redo ── */}
            <div className="flex items-center gap-1 min-w-0 flex-1">
              <button
                type="button"
                onClick={onBack}
                aria-label={t('optimizer.backToResume', 'Back to Resume')}
                className="flex items-center gap-1.5 min-h-[34px] px-2.5 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors touch-manipulation shrink-0"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" />
                <span className="hidden md:inline">{t('optimizer.backToResume', 'Back to Resume')}</span>
              </button>

              <span className="hidden md:block w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1 shrink-0" aria-hidden />

              <button
                type="button"
                onClick={handleUndo}
                disabled={undoState.past.length === 0}
                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/60 disabled:opacity-30 disabled:pointer-events-none transition-colors touch-manipulation shrink-0"
                aria-label={t('optimizer.undo', 'Undo')}
                title={t('optimizer.undo', 'Undo')}
              >
                <Undo2 className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleRedo}
                disabled={undoState.future.length === 0}
                className="p-1.5 rounded-md text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700/60 disabled:opacity-30 disabled:pointer-events-none transition-colors touch-manipulation shrink-0"
                aria-label={t('optimizer.redo', 'Redo')}
                title={t('optimizer.redo', 'Redo')}
              >
                <Redo2 className="w-4 h-4" />
              </button>
            </div>

            {/* ── Right group: Save · Start New · Continue ── */}
            <div className="flex items-center gap-1.5 shrink-0">
              {onSave && (
                <button
                  type="button"
                  onClick={handleSave}
                  className="hidden md:flex items-center gap-1.5 min-h-[34px] px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                >
                  {saveFeedback === 'saved' ? (
                    <span className="text-green-600 dark:text-green-400">{t('optimizer.saved', 'Saved')}</span>
                  ) : (
                    <>
                      <Save className="w-3.5 h-3.5 shrink-0" />
                      <span>{t('optimizer.saveShort', 'Save')}</span>
                    </>
                  )}
                </button>
              )}
              {onStartNew && (
                <button
                  type="button"
                  onClick={onStartNew}
                  className="hidden md:flex items-center gap-1.5 min-h-[34px] px-3 py-1.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors touch-manipulation"
                  title={t('optimizer.startNew', 'Start New')}
                >
                  <RefreshCw className="w-3.5 h-3.5 shrink-0" />
                  <span>{t('optimizer.startNew', 'Start New')}</span>
                </button>
              )}
              <button
                type="button"
                onClick={handleContinue}
                aria-label={t('optimizer.continueToJobUrl', 'Continue to Job URL')}
                className={`flex min-h-[34px] touch-manipulation items-center gap-1.5 border-0 px-3 py-1.5 text-sm font-semibold active:scale-95 sm:px-4 ${STEP_PRIMARY_CTA_CLASS}`}
              >
                <span>{t('optimizer.continueToJobUrl', 'Continue to Job URL')}</span>
                <ArrowRight className="w-4 h-4 shrink-0" />
              </button>
            </div>

            {preprocessError && (
              <p className="absolute bottom-0 left-0 right-0 text-xs text-amber-600 dark:text-amber-400 px-4 pb-1" role="alert">{preprocessError}</p>
            )}
          </div>
        )}

        {/* Top mobile tab bar removed — moved to KickResume-style sticky bottom nav below */}


        {/* Two-pane: each pane scrolls independently so section selection can bring both into view */}
        <div
          ref={paneContainerRef}
          className="flex-1 min-h-0 flex flex-col md:flex-row w-full overflow-hidden"
        >
          {hasSections && sections.length > 0 && (
            <React.Fragment>
            <div
              className={`flex flex-col min-h-0 px-3 py-2 md:pl-4 md:pr-2 md:py-3 md:max-w-[65%] md:items-stretch md:min-w-0 md:flex-shrink-0 ${
                !isMd ? (mobileTab === 'sections' ? 'flex flex-1 w-full' : 'hidden') : ''
              }`}
              style={{
                width: isMd ? `${leftPaneWidthPct}%` : '100%',
                ...(isMd && { minWidth: 380, maxWidth: '65%' }),
              }}
            >
                {/* Section Analysis – sub-header with branded accent bar */}
                <div className="flex-shrink-0 px-1 pt-2 pb-1.5 flex items-center justify-between gap-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block w-1 h-5 rounded-full shrink-0"
                      style={{ background: 'var(--brand-cta-bg)' }}
                      aria-hidden
                    />
                    <h2 className="text-sm font-semibold vscode-text-primary">
                      {t('optimizer.sectionAnalysis', 'Section Analysis')}
                    </h2>
                    <span className="text-xs vscode-text-tertiary">
                      ({sections.length})
                    </span>
                  </div>
                  {/* Mobile-only Save shortcut – on md+ Save lives in the top bar */}
                  {onSave && !isMd && (
                    <button
                      type="button"
                      onClick={handleSave}
                      className="flex items-center gap-1.5 min-h-[32px] px-2.5 py-1.5 text-xs font-medium rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors shrink-0"
                    >
                      {saveFeedback === 'saved' ? (
                        <span className="text-green-600 dark:text-green-400">{t('optimizer.saved', 'Saved')}</span>
                      ) : (
                        <>
                          <Save className="w-3.5 h-3.5 shrink-0" />
                          <span>{t('optimizer.saveShort', 'Save')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
                {/* Your sections – scrollable list, distinct block */}
                <div ref={leftPaneScrollRef} className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide min-w-0">
                  <div className="py-1 px-1 space-y-2 min-w-0">
                  {sections.map((section, i) => {
                    const isExpanded = expandedSectionIndex === i;
                    const title = displaySectionTitle(section, i, tWithFallback);
                    return (
                      <div
                        key={`${section.title}-${i}`}
                        id={`section-accordion-${i}`}
                        className="group rounded-lg border border-gray-200/60 dark:border-gray-600/60 bg-gray-50/50 dark:bg-gray-800/40 overflow-visible hover:border-gray-200 dark:hover:border-gray-600 hover:bg-white dark:hover:bg-gray-800/60 transition-colors"
                      >
                        <div className="flex items-center gap-1 px-2.5 py-2 min-h-[44px]">
                          <button
                            type="button"
                            onClick={() => toggleSection(i)}
                            className="flex-1 flex items-center gap-2.5 min-w-0 py-1 px-1 text-left rounded-md hover:bg-transparent"
                          >
                            <div className="w-8 h-8 rounded-lg bg-white dark:bg-gray-700/80 border border-gray-200/80 dark:border-gray-600 flex items-center justify-center shrink-0 text-gray-500 dark:text-gray-400">
                              {sectionIcon(title, 'w-4 h-4 shrink-0')}
                            </div>
                            <span className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                              {title}
                            </span>
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 shrink-0 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeSection(i); }}
                            className="p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100 focus:outline-none"
                            title={t('optimizer.removeSection', 'Remove section')}
                            aria-label={t('optimizer.removeSection', 'Remove section')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {isExpanded && (
                          <div className="border-t border-gray-100 dark:border-gray-600/60 p-3 md:p-4 bg-white dark:bg-gray-900/50 rounded-b-lg overflow-x-hidden">
                          {isPersonalInfoSection(section, i) ? (
                            <PersonalInfoForm
                              content={section.content}
                              sectionTitle={title}
                              onContentChange={(newContent) => updateSectionContent(i, newContent)}
                            />
                          ) : isEducationSection(section) ? (
                            <EducationCard
                              content={section.content}
                              sectionTitle={title}
                              onContentChange={(newContent) => updateSectionContent(i, newContent)}
                            />
                          ) : isWorkExperienceSection(section) ? (
                            <WorkExperienceCard
                              content={section.content}
                              sectionTitle={title}
                              onContentChange={(newContent) => updateSectionContent(i, newContent)}
                            />
                          ) : isAwardsSection(section) ? (
                            <AwardsCard
                              content={section.content}
                              sectionTitle={title}
                              onContentChange={(newContent) => updateSectionContent(i, newContent)}
                            />
                          ) : (
                            <div className="flex flex-col">
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                                {t('optimizer.sectionTitle', 'Section title')}
                              </label>
                              <input
                                type="text"
                                value={section.title}
                                onChange={(e) => updateSectionTitle(i, e.target.value)}
                                className="w-full px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg mb-4 bg-white dark:bg-gray-800"
                              />
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                                {t('optimizer.sectionContent', 'Section content')}
                              </label>
                              <p className="text-xs text-gray-400 dark:text-gray-500 mb-1.5">
                                {t('optimizer.formattingHint', 'Select text, then click Bold/Italic/Strikethrough/Underline. Formatting appears in both editor and preview.')}
                              </p>
                              <div className="rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden bg-white dark:bg-gray-800">
                                <RichTextToolbar
                                  textareaId={`section-content-${i}`}
                                  value={section.content}
                                  onChange={(v) => updateSectionContent(i, v)}
                                />
                                <MarkdownSectionEditor
                                  id={`section-content-${i}`}
                                  value={section.content}
                                  onChange={(v) => updateSectionContent(i, v)}
                                  minHeight="200px"
                                  className="focus:ring-0 border-0"
                                  placeholder={t('optimizer.sectionContentPlaceholder', 'Content…')}
                                />
                              </div>
                            </div>
                          )}
                          {onSave && (
                            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-600/60 flex justify-end">
                              <button
                                type="button"
                                onClick={() => handleSaveFromSection(i)}
                                className="flex items-center gap-1.5 min-h-[36px] px-4 py-2 text-sm font-medium rounded-xl text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60 transition-colors border border-gray-200 dark:border-gray-600"
                              >
                                {saveFeedback === 'saved' && savedSectionIndex === i ? (
                                  <span className="text-green-600 dark:text-green-400">{t('optimizer.saved', 'Saved')}</span>
                                ) : (
                                  <>
                                    <Save className="w-3.5 h-3.5 shrink-0" />
                                    <span>{t('optimizer.saveShort', 'Save')}</span>
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
                  </div>

                  {/* Divider between section list and Add New Section – two distinct blocks */}
                  <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-600 my-4" aria-hidden />

                  {/* Mobile: compact "+ Add Section" row that opens the bottom sheet */}
                  {!isMd && (
                    <div className="pb-3">
                      <button
                        type="button"
                        onClick={() => setMobileAddSectionOpen(true)}
                        className="w-full flex items-center justify-center gap-2 min-h-[44px] px-4 py-2.5 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-600 dark:text-gray-400 hover:border-[#6C47FF]/50 hover:text-[#6C47FF] dark:hover:border-[#5B8DFF]/50 dark:hover:text-[#5B8DFF] transition-colors touch-manipulation"
                      >
                        <Plus className="w-4 h-4 shrink-0" />
                        {t('optimizer.addNewSection', '+ Add New Section')}
                      </button>
                    </div>
                  )}

                  {/* Add New Section – second block, clearly separated. On mobile, this block is hidden:
                      the "Add Section" action is exposed via the bottom nav sheet instead. */}
                  <div className={`pb-4 ${!isMd ? 'hidden' : ''}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {t('optimizer.addNewSection', '+ Add New Section')}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPreviewAllPopupOpen(true)}
                        className="flex items-center gap-1.5 px-2 py-1 text-[11px] font-medium rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700/60 hover:text-gray-800 dark:hover:text-gray-200 transition-colors border-0 shrink-0"
                        title={t('optimizer.previewAllSections', 'Preview all sections')}
                      >
                        <LayoutGrid className="w-3.5 h-3.5 shrink-0" />
                        {t('optimizer.previewAllSections', 'Preview All Sections')}
                      </button>
                    </div>
                    <div className="flex gap-0 min-h-0 min-w-0 overflow-x-auto overflow-y-hidden items-start">
                      <div className="min-w-[140px] w-max max-w-full space-y-0 max-h-[320px] overflow-y-auto overflow-x-hidden shrink-0 scrollbar-neutral">
                        {ADDABLE_SECTION_TYPES.map((type) => {
                          const isHovered = hoveredAddSection === type.title;
                          return (
                            <div
                              key={type.title}
                              role="button"
                              tabIndex={0}
                              onClick={() => addSection(type.title)}
                              onMouseEnter={() => setHoveredAddSection(type.title)}
                              onMouseLeave={() => setHoveredAddSection(null)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSection(type.title); } }}
                              className={`flex items-center gap-1 py-1 px-1.5 rounded-md cursor-pointer transition-colors min-h-[36px] ${isHovered ? 'bg-gray-100 dark:bg-gray-700/50' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                              aria-label={t('optimizer.addSection', 'Add {{title}}', { title: type.title })}
                            >
                              <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/80">
                                {sectionIcon(type.title)}
                              </div>
                              <span className="flex-1 min-w-0 text-xs text-gray-700 dark:text-gray-200 truncate">
                                {type.title}
                              </span>
                              {isHovered && (
                                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#6C47FF]/15 text-[#6C47FF] dark:text-[#5B8DFF] shrink-0">
                                  <Plus className="w-2.5 h-2.5" strokeWidth={2.5} />
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {hoveredAddSection && (() => {
                        const type = ADDABLE_SECTION_TYPES.find((s) => s.title === hoveredAddSection);
                        if (!type) return null;
                        return (
                          <div
                            className="relative z-10 flex flex-shrink-0 w-[420px] min-w-[380px] max-w-[480px] min-h-[420px] flex-col rounded-2xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/95 shadow-xl"
                            onMouseEnter={() => setHoveredAddSection(type.title)}
                            onMouseLeave={() => setHoveredAddSection(null)}
                          >
                            {/* Card header – section title */}
                            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-600 shrink-0">
                              <span className="text-base font-semibold text-gray-800 dark:text-gray-100">
                                {type.title}
                              </span>
                            </div>
                            {/* Body: description, sub-heading, sample block – same layout, larger size */}
                            <div className="flex-1 min-h-[360px] overflow-y-auto overflow-x-hidden p-5">
                              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-5">
                                {type.description}
                              </p>
                              <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
                                {type.title}
                              </p>
                              <div className="rounded-xl bg-gray-50 dark:bg-gray-700/50 p-4 border border-gray-100 dark:border-gray-600 min-h-[140px]">
                                <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line leading-relaxed break-words">
                                  {type.sampleText}
                                </p>
                              </div>
                              {type.howToLabel && (
                                <a href="#" onClick={(e) => e.preventDefault()} className="inline-flex items-center gap-0.5 mt-4 text-sm text-[#6C47FF] dark:text-[#5B8DFF] hover:underline break-words">
                                  <span className="text-gray-400">?</span> {type.howToLabel}
                                </a>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                  {/* Preview All Sections – scrollable, compact, clearly visible popup (Kickresume-style) */}
                  {previewAllPopupOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 bg-black/55 backdrop-blur-sm" onClick={() => setPreviewAllPopupOpen(false)} role="dialog" aria-modal="true" aria-label={t('optimizer.selectSectionToAdd', 'Select a section to add')}>
                      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-600 flex flex-col max-h-[82vh] w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between shrink-0 py-2.5 px-3 border-b border-gray-100 dark:border-gray-700">
                          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                            {t('optimizer.selectSectionToAdd', 'Select a section to add')}
                          </h3>
                          <button type="button" onClick={() => setPreviewAllPopupOpen(false)} className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" aria-label={t('common.close', 'Close')}>
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-3 overscroll-contain">
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {ADDABLE_SECTION_TYPES.map((type) => (
                              <button
                                key={type.title}
                                type="button"
                                onClick={() => { addSection(type.title); setPreviewAllPopupOpen(false); scrollPreviewToTop(); }}
                                className="flex flex-col text-left rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800/95 shadow-sm hover:shadow-md hover:border-[#6C47FF]/40 dark:hover:border-[#5B8DFF]/40 transition-all overflow-hidden min-h-0"
                                aria-label={t('optimizer.addSection', 'Add {{title}}', { title: type.title })}
                              >
                                <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100 dark:border-gray-600 shrink-0">
                                  <div className="w-5 h-5 rounded flex items-center justify-center shrink-0 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/80">
                                    {sectionIcon(type.title)}
                                  </div>
                                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{type.title}</span>
                                </div>
                                <div className="flex-1 min-h-[64px] overflow-y-auto p-1.5">
                                  <p className="text-[10px] text-gray-600 dark:text-gray-400 leading-snug line-clamp-2">{type.description}</p>
                                  <div className="mt-1 rounded bg-gray-50 dark:bg-gray-700/50 p-1 border border-gray-100 dark:border-gray-600">
                                    <p className="text-[9px] text-gray-600 dark:text-gray-400 whitespace-pre-line leading-relaxed line-clamp-3">{type.sampleText}</p>
                                  </div>
                                  {type.howToLabel && (
                                    <span className="inline-block mt-0.5 text-[9px] text-[#6C47FF] dark:text-[#5B8DFF] truncate max-w-full">? {type.howToLabel}</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
            {/* Resize strip: draggable divider with grip */}
            <div
              role="separator"
              aria-label={t('optimizer.resizePanes', 'Resize panes')}
              className="hidden md:flex flex-shrink-0 w-3 min-h-full bg-gray-200 dark:bg-gray-600 border-x border-gray-300 dark:border-gray-500 cursor-col-resize hover:bg-gray-300 dark:hover:bg-gray-500 active:bg-[#6C47FF]/20 dark:active:bg-[#5B8DFF]/20 select-none touch-none flex-col items-center justify-center"
              onPointerDown={(e) => { e.preventDefault(); setIsDraggingDivider(true); }}
            >
              <GripVertical className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" aria-hidden />
            </div>
            </React.Fragment>
          )}

          {/* Right pane: preview only – no second nav strip; vertical bar divides section list and preview */}
          <div
            ref={previewRef}
            className={`relative flex flex-col min-w-0 min-h-0 md:min-w-[320px] ${
              isNative ? '' : 'bg-gray-50/50 dark:bg-gray-900/40'
            } ${
              !isMd ? (mobileTab === 'preview' ? 'flex flex-1 w-full' : 'hidden') : 'flex flex-1 min-h-[320px]'
            }`}
            style={isNative ? { background: 'var(--ggj-bg-page)' } : undefined}
            aria-label={t('optimizer.resumePreview', 'Resume preview')}
          >
            <div ref={rightPaneScrollRef} className="flex-1 min-h-0 flex flex-col min-w-0 overflow-y-auto overflow-x-hidden scrollbar-neutral" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="pt-3 pb-6 px-3 md:px-4 min-h-0 flex-1 relative">
              <div
                className={[
                  isNative ? 'resume-preview-zoom-wrapper overflow-hidden' : 'resume-preview-zoom-wrapper min-h-[400px] overflow-hidden',
                  isNative
                    ? 'rounded-2xl bg-white'
                    : 'rounded-2xl border border-gray-200/80 dark:border-gray-600/80 bg-white dark:bg-gray-800/60 shadow-sm',
                  'resume-preview',
                  `resume-preview--${getTemplateSkinClass(activeTemplateId)}`,
                  designColor ? 'resume-preview--custom-accent' : '',
                  designFont === 'serif'   ? 'resume-preview--font-serif'   : '',
                  designFont === 'compact' ? 'resume-preview--font-compact' : '',
                  `resume-preview--caps-${designCaps}`,
                ].filter(Boolean).join(' ')}
                style={(() => {
                  const colorEntry = DESIGN_COLORS.find(c => c.swatch === designColor);
                  return {
                    transform: `scale(${previewZoom})`,
                    transformOrigin: 'top center',
                    transition: 'transform 0.12s ease-out',
                    ...(isNative ? { boxShadow: '0 12px 48px rgba(0,0,0,0.55), 0 4px 16px rgba(0,0,0,0.35)' } : {}),
                    ...(colorEntry ? {
                      '--resume-accent':        colorEntry.accent,
                      '--resume-accent-bg':     colorEntry.accentBg,
                      '--resume-accent-text':   colorEntry.accentText,
                      '--resume-accent-border': colorEntry.accentBorder,
                    } as React.CSSProperties : {}),
                  };
                })()}
              >
                {hasSections ? (
                  <div className="max-w-2xl mx-auto resume-preview-document-inner pb-8">
                    {sections.map((section, i) => {
                      const isSelected = expandedSectionIndex === i;
                      const title = displaySectionTitle(section, i, tWithFallback);
                      const isContact = isPersonalInfoSection(section, i);
                      if (!isSectionVisibleInPreview(i)) return <div key={`${section.title}-${i}`} className="hidden" />;
                      return (
                        <div
                          key={`${section.title}-${i}`}
                          id={`preview-section-${i}`}
                          role="button"
                          tabIndex={0}
                          onClick={() => focusSectionFromPreview(i)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); focusSectionFromPreview(i); } }}
                          className={`resume-preview-section-wrapper scroll-mt-4 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-[#6C47FF]/50 dark:focus-visible:ring-[#5B8DFF]/50 ${isSelected ? 'ring-2 ring-[#6C47FF] dark:ring-[#5B8DFF] rounded-lg' : ''}`}
                          aria-label={t('optimizer.clickToEditSection', 'Click to edit this section')}
                        >
                          <div className={`resume-preview-section-card overflow-hidden${isContact ? ' resume-preview-contact-card' : ''}`}>
                            <div className="resume-preview-section-header">
                              <h4 style={{ margin: 0 }}>{title}</h4>
                            </div>
                            <div className="resume-preview-section-body">
                              <SectionContentWithFormatting key={`preview-${i}-${(section.content || '').length}`} content={getStructuredSectionDisplayContent(section) || section.content} sectionTitle={section.title} />
                            </div>
                          </div>
                          {isSelected && (
                            <div className="mt-2 pt-2 border-t border-[#6C47FF]/30 dark:border-[#5B8DFF]/30 text-xs font-medium text-[#6C47FF] dark:text-[#5B8DFF] px-2">
                              {t('optimizer.clickToEditSection', 'Click to edit this section')}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="max-w-2xl mx-auto resume-preview-document-inner pb-8">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2 px-2">
                      {t('optimizer.reviewSectionsSingleBlock', 'Your resume is shown as one block below.')}
                    </p>
                    <div className="resume-preview-section-wrapper">
                      <div className="resume-preview-section-card overflow-hidden">
                        <div className="resume-preview-section-header">
                          <h4 style={{ margin: 0 }}>FULL RESUME</h4>
                        </div>
                        <div className="resume-preview-section-body">
                          <SectionContentWithFormatting key={`preview-single-${(sections[0]?.content || '').length}`} content={sections[0]?.content || resumeData.parsedText || ''} sectionTitle="Full Resume" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </div>

        {/* ══ NATIVE DESIGN DRAWER — templates + styling, sits ABOVE bottom nav ══ */}
        {activePanel === 'design' && isNative && !isMd && (
          <div
            className="flex-shrink-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-8px_32px_rgba(0,0,0,0.14)]"
            style={{
              maxHeight: designSheetExpanded ? '64vmax' : '230px',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1)',
            }}
            role="region"
            aria-label="Design"
          >
            {/* Drag handle — tap to expand/collapse, drag down to collapse / close */}
            <button
              type="button"
              className="w-full flex justify-center pt-3 pb-2.5 flex-shrink-0 touch-manipulation select-none"
              onClick={() => setDesignSheetExpanded(p => !p)}
              onTouchStart={(e) => { dragStartY.current = e.touches[0].clientY; }}
              onTouchEnd={(e) => {
                if (dragStartY.current === null) return;
                const delta = e.changedTouches[0].clientY - dragStartY.current;
                dragStartY.current = null;
                if (delta > 80) {
                  if (designSheetExpanded) {
                    setDesignSheetExpanded(false);   // collapse first
                  } else {
                    setActivePanel(null);             // already collapsed → close drawer
                  }
                } else if (delta < -60) {
                  setDesignSheetExpanded(true);       // drag up → expand
                }
              }}
              aria-label={designSheetExpanded ? 'Collapse' : 'Expand'}
            >
              <div className="w-14 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
            </button>

            {/* Section label */}
            <div className="flex items-center justify-between px-4 pb-2 flex-shrink-0">
              <span className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                {designSheetExpanded ? 'Design' : 'Templates · tap to apply'}
              </span>
              <span className="text-[10px] text-gray-400">{PICKER_TEMPLATE_IDS.length} templates</span>
            </div>

            {/* Template thumbnails — ALWAYS visible, horizontal scroll */}
            <div
              className="overflow-x-auto flex-shrink-0 px-3 pb-3"
              style={{ WebkitOverflowScrolling: 'touch' as any }}
            >
              <div className="flex gap-3" style={{ width: 'max-content' }}>
                {PICKER_TEMPLATE_IDS.map((tid) => {
                  const tpl = TEMPLATE_LIBRARY.find(t => t.id === tid);
                  if (!tpl) return null;
                  return (
                    <div key={tid} style={{ width: 110, flexShrink: 0 }}>
                      <TemplateThumbnail
                        tpl={{ id: tpl.id, label: tpl.label, ats: tpl.ats }}
                        selected={activeTemplateId === tpl.id}
                        onClick={() => setActiveTemplateId(tpl.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Expanded: Heading / Color / Font options — all wired to live state */}
            {designSheetExpanded && (
              <div className="flex-1 overflow-y-auto px-4 pb-5 space-y-5 border-t border-gray-100 dark:border-gray-700 pt-4">
                {/* Accent Color */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2.5">
                    Accent Color {designColor && <span className="normal-case font-normal text-[9px] ml-1 text-[#6C47FF]">— {DESIGN_COLORS.find(c => c.swatch === designColor)?.label}</span>}
                  </p>
                  <div className="flex gap-2.5 flex-wrap">
                    {/* "None" reset chip */}
                    <button type="button"
                      onClick={() => setDesignColor(null)}
                      className="flex items-center justify-center w-8 h-8 rounded-full border-2 touch-manipulation transition-transform active:scale-90"
                      style={{
                        background: '#f3f4f6',
                        borderColor: designColor === null ? '#6C47FF' : '#e5e7eb',
                        boxShadow: designColor === null ? '0 0 0 2px white, 0 0 0 4px #6C47FF' : 'none',
                      }}
                      title="Template default"
                    >
                      <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700 }}>—</span>
                    </button>
                    {DESIGN_COLORS.map((c) => {
                      const isActive = designColor === c.swatch;
                      return (
                        <button key={c.swatch} type="button"
                          onClick={() => setDesignColor(isActive ? null : c.swatch)}
                          className="w-8 h-8 rounded-full border-2 touch-manipulation transition-transform active:scale-90"
                          style={{
                            background: c.swatch,
                            borderColor: isActive ? c.swatch : 'transparent',
                            boxShadow: isActive ? `0 0 0 2px white, 0 0 0 4px ${c.swatch}` : 'none',
                          }}
                          title={c.label}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Heading Capitalization */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Heading Style</p>
                  <div className="flex gap-2">
                    {(['title', 'upper'] as const).map((cap) => {
                      const isActive = designCaps === cap;
                      return (
                        <button key={cap} type="button"
                          onClick={() => setDesignCaps(cap)}
                          className="flex-1 h-10 rounded-xl border-2 text-sm font-bold touch-manipulation"
                          style={{
                            borderColor: isActive ? '#6C47FF' : '#e2e8f0',
                            background: isActive ? 'rgba(108,71,255,0.06)' : 'transparent',
                            color: isActive ? '#6C47FF' : '#9ca3af',
                          }}>
                          {cap === 'title' ? 'Aa' : 'AA'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Font */}
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Font</p>
                  <div className="flex gap-2">
                    {(['default', 'serif', 'compact'] as const).map((f) => {
                      const isActive = designFont === f;
                      const label = f === 'default' ? 'Default' : f === 'serif' ? 'Serif' : 'Compact';
                      return (
                        <button key={f} type="button"
                          onClick={() => setDesignFont(f)}
                          className="flex-1 h-10 rounded-xl border-2 text-xs font-semibold touch-manipulation"
                          style={{
                            borderColor: isActive ? '#6C47FF' : '#e2e8f0',
                            background: isActive ? 'rgba(108,71,255,0.06)' : 'transparent',
                            color: isActive ? '#6C47FF' : '#9ca3af',
                          }}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ NATIVE ADD SECTION DRAWER — sits ABOVE bottom nav ══ */}
        {activePanel === 'addsection' && isNative && !isMd && (
          <div
            className="flex-shrink-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-[0_-8px_32px_rgba(0,0,0,0.14)]"
            style={{ maxHeight: '52vmax', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            role="region"
            aria-label="Add Section"
          >
            <div className="flex justify-center pt-2.5 pb-1 flex-shrink-0">
              <div className="w-12 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600" />
            </div>
            <div className="flex items-center px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
              <span className="text-sm font-bold" style={{ color: 'var(--ggj-text-heading)' }}>Add New Section</span>
            </div>
            <div className="overflow-y-auto flex-1 p-3">
              <div className="grid grid-cols-2 gap-2">
                {ADDABLE_SECTION_TYPES.map((type) => (
                  <button key={type.title} type="button"
                    onClick={() => { addSection(type.title); setActivePanel(null); setMobileTab('sections'); scrollPreviewToTop(); }}
                    className="flex items-center gap-2.5 px-3 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-left min-h-[52px] touch-manipulation">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-500">{sectionIcon(type.title, 'w-3.5 h-3.5')}</div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{type.title}</p>
                      <p className="text-[10px] text-gray-400 truncate">{type.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Bottom: Back/Next row + Fill In | Design | Add Section (always on top of scroll) ── */}
        {!embedded && !isMd && isNative && (
          <div
            className="flex-shrink-0 flex flex-col bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
            style={{
              position: 'relative' as const,
              zIndex: 200,
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 0px))',
            }}
          >
            {/* Navigation row: Back | Next */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
              <button type="button" onClick={onBack} className="flex items-center gap-1.5 min-h-[40px] px-3 rounded-xl text-gray-600 dark:text-gray-300 text-sm font-semibold touch-manipulation bg-gray-100 dark:bg-gray-800">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button type="button" onClick={handleContinue} className="flex items-center gap-1.5 min-h-[40px] px-4 rounded-xl bg-[#6C47FF] text-white text-sm font-bold touch-manipulation shadow-sm">
                Next <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            {/* Fill In | Design | Add Section */}
            <div className="flex items-center">
              <button type="button"
                onClick={() => { setMobileTab('sections'); setActivePanel(null); setDesignSheetExpanded(false); }}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[52px] text-[10px] font-medium touch-manipulation transition-colors ${mobileTab === 'sections' && activePanel === null ? 'text-[#6C47FF]' : 'text-gray-500 dark:text-gray-400'}`}>
                <FileText className="w-5 h-5" />Fill In
                {mobileTab === 'sections' && activePanel === null && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF]" />}
              </button>
              <button type="button"
                onClick={() => { setActivePanel(p => p === 'design' ? null : 'design'); setMobileTab('preview'); if (activePanel === 'design') setDesignSheetExpanded(false); }}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[52px] text-[10px] font-medium touch-manipulation transition-colors ${activePanel === 'design' ? 'text-[#6C47FF]' : 'text-gray-500 dark:text-gray-400'}`}>
                <Palette className="w-5 h-5" />Design
                {activePanel === 'design' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF]" />}
              </button>
              <button type="button"
                onClick={() => setActivePanel(p => p === 'addsection' ? null : 'addsection')}
                className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[52px] text-[10px] font-medium touch-manipulation transition-colors ${activePanel === 'addsection' ? 'text-[#6C47FF]' : 'text-gray-500 dark:text-gray-400'}`}>
                <Plus className="w-5 h-5" />Add Section
                {activePanel === 'addsection' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF]" />}
              </button>
            </div>
          </div>
        )}

        {/* ── Bottom nav: web / non-native (unchanged) ── */}
        {!embedded && !isMd && !isNative && (
          <div className="relative flex-shrink-0 flex items-center border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" style={{ paddingBottom: 12 }}>
            {/* Fill In tab */}
            <button
              type="button"
              onClick={() => setMobileTab('sections')}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-medium touch-manipulation transition-colors ${
                mobileTab === 'sections'
                  ? 'text-[#6C47FF] dark:text-[#5B8DFF]'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <FileText className="w-5 h-5 shrink-0" aria-hidden />
              Fill In
              {mobileTab === 'sections' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF] dark:bg-[#5B8DFF]" />}
            </button>
            {/* Preview tab */}
            <button
              type="button"
              onClick={() => setMobileTab('preview')}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-medium touch-manipulation transition-colors ${
                mobileTab === 'preview'
                  ? 'text-[#6C47FF] dark:text-[#5B8DFF]'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Eye className="w-5 h-5 shrink-0" aria-hidden />
              Preview
              {mobileTab === 'preview' && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF] dark:bg-[#5B8DFF]" />}
            </button>
            {/* Templates tab */}
            <button
              type="button"
              onClick={() => {
                const opening = !templatesDrawerOpen;
                setTemplatesDrawerOpen(opening);
                if (opening) { setMobileTab('preview'); setMobileAddSectionOpen(false); }
              }}
              className={`relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-medium touch-manipulation transition-colors ${
                templatesDrawerOpen
                  ? 'text-[#6C47FF] dark:text-[#5B8DFF]'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              <Palette className="w-5 h-5 shrink-0" aria-hidden />
              Templates
              {templatesDrawerOpen && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-[#6C47FF] dark:bg-[#5B8DFF]" />}
            </button>
            {/* Add Section */}
            <button
              type="button"
              onClick={() => setMobileAddSectionOpen(true)}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-medium text-gray-500 dark:text-gray-400 touch-manipulation transition-colors hover:text-gray-700 dark:hover:text-gray-200"
            >
              <Plus className="w-5 h-5 shrink-0" aria-hidden />
              Add Section
            </button>
            {/* Continue */}
            <button
              type="button"
              onClick={handleContinue}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 min-h-[56px] text-[10px] font-semibold text-[#6C47FF] dark:text-[#5B8DFF] touch-manipulation transition-colors hover:opacity-80"
            >
              <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
              Continue
            </button>
          </div>
        )}

        {/* ── Web add-section modal (non-native only) ── */}
        {mobileAddSectionOpen && !isMd && !isNative && (
          <div className="fixed inset-0 z-[9000] flex flex-col justify-end" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileAddSectionOpen(false)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-t-2xl shadow-2xl max-h-[72vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="flex-shrink-0 flex justify-center pt-2.5 pb-1"><div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" /></div>
              <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                <span className="text-base font-semibold text-gray-900 dark:text-white">Add New Section</span>
                <button type="button" onClick={() => setMobileAddSectionOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="grid grid-cols-2 gap-2">
                  {ADDABLE_SECTION_TYPES.map((type) => (
                    <button key={type.title} type="button"
                      onClick={() => { addSection(type.title); setMobileAddSectionOpen(false); setMobileTab('sections'); scrollPreviewToTop(); }}
                      className="flex items-center gap-2.5 px-3 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-left min-h-[52px] touch-manipulation">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-gray-500 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">{sectionIcon(type.title, 'w-3.5 h-3.5')}</div>
                      <div className="min-w-0"><p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{type.title}</p></div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Web templates drawer (non-native only) ── */}
        {templatesDrawerOpen && !isMd && !isNative && (
          <div className="flex-shrink-0 bg-white dark:bg-gray-900 border-t-2 border-[#6C47FF]/30 shadow-[0_-8px_32px_rgba(108,71,255,0.15)]"
            style={{ maxHeight: templatesDrawerExpanded ? '58vh' : '240px', transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <button type="button" className="w-full flex justify-center pt-2 pb-1 flex-shrink-0" onClick={() => setTemplatesDrawerExpanded(p => !p)}>
              <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
            </button>
            <div className="overflow-x-auto px-3 pb-3 flex-1" style={{ WebkitOverflowScrolling: 'touch' as any }}>
              <div className="flex gap-3" style={{ width: 'max-content' }}>
                {PICKER_TEMPLATE_IDS.map((tid) => {
                  const tpl = TEMPLATE_LIBRARY.find(t => t.id === tid);
                  if (!tpl) return null;
                  return (
                    <div key={tid} style={{ width: 110, flexShrink: 0 }}>
                      <TemplateThumbnail
                        tpl={{ id: tpl.id, label: tpl.label, ats: tpl.ats }}
                        selected={activeTemplateId === tpl.id}
                        onClick={() => setActiveTemplateId(tpl.id)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

    </motion.div>
  );
};

export default ParsedResumeStep;
